import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';

const HeroSection = () => {
  const imageRefs = useRef([]);

  // Parallax effect on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      imageRefs.current.forEach((img, index) => {
        if (img) {
          const speed = 0.05 * (index + 1);
          img.style.transform = `translateY(${scrolled * speed}px)`;
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Main Hero Section */}
      <section className="relative min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary-50 overflow-hidden">
        
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-accent-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float" style={{ animationDelay: '1s' }} />
        </div>

        <div className="container mx-auto px-4 py-16 md:py-20 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[calc(100vh-8rem)]">
            
            {/* Left: Content */}
            <div className="space-y-8 animate-slide-in">
              
              {/* Premium Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-primary-200 shadow-sm">
                <Sparkles className="w-4 h-4 text-accent-600" />
                <span className="text-sm font-semibold text-primary-700 tracking-wide">
                  ELEVATE YOUR HOME AFFORDABLY
                </span>
              </div>

              {/* Main Heading */}
              <div className="space-y-4">
                <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-display font-bold text-dark-900 leading-[0.95] tracking-tight">
                  QUALITY
                  <span className="block text-primary-600 mt-2">
                    FURNITURE
                  </span>
                </h1>
                <p className="text-2xl md:text-3xl lg:text-4xl text-dark-600 font-light leading-relaxed">
                  For Every Canadian Home
                </p>
              </div>

              {/* Description */}
              <p className="text-lg md:text-xl text-dark-500 leading-relaxed max-w-xl">
                Premium dressers, wadrobes, tall storage units, wooden beds, vanities and customized furniture at wholesale prices. Quality you can trust, delivered across Canada.
              </p>

              {/* CTA Button */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link
                  to="/shop"
                  className="group inline-flex items-center justify-center gap-3 px-10 py-5 bg-primary-600 text-white text-lg font-semibold rounded-xl hover:bg-primary-700 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl shadow-xl"
                >
                  Explore Collection
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center gap-1 md:gap-8 pt-8 border-t border-gray-200">
                <div className="text-center">
                  <p className="text-2xl md:text-3xl font-bold text-dark-900">500+</p>
                  <p className="text-sm text-dark-500 mt-1">Products</p>
                </div>
                <div className='md:hidden font-bold text-dark-900'>|</div>
              
                <div className="text-center">
                  <p className="text-2xl md:text-3xl font-bold text-dark-900">5K+</p>
                  <p className="text-sm text-dark-500 mt-1">Happy Customers</p>
                </div>
                <div className='md:hidden font-bold text-dark-900'>|</div>
                <div className="text-center">
                  <p className="text-2xl md:text-3xl font-bold text-dark-900">4.6★</p>
                  <p className="text-sm text-dark-500 mt-1">Average Rating</p>
                </div>
              </div>
            </div>

            {/* Right: Product Mosaic */}
            <div className="relative h-[600px] lg:h-[700px] animate-fade-in" style={{ animationDelay: '0.3s' }}>
              
              {/* Main Large Image - Bedroom */}
              <div 
                ref={el => imageRefs.current[0] = el}
                className="absolute top-0 right-0 w-[70%] h-[55%] rounded-3xl overflow-hidden shadow-2xl group transition-transform duration-700"
              >
                <img
                  src="https://images.unsplash.com/photo-1617806118233-18e1de247200?w=800&h=800&fit=crop&q=90"
                  alt="Modern bedroom furniture"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>

              {/* Medium Image - Dining */}
              <div 
                ref={el => imageRefs.current[1] = el}
                className="absolute bottom-[45%] left-0 w-[55%] h-[40%] rounded-3xl overflow-hidden shadow-2xl group transition-transform duration-700"
                style={{ animationDelay: '0.5s' }}
              >
                <img
                  src="https://images.unsplash.com/photo-1615529182904-14819c35db37?w=600&h=600&fit=crop&q=90"
                  alt="Elegant dining furniture"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>

              {/* Small Image - Wardrobe/Storage */}
              <div 
                ref={el => imageRefs.current[2] = el}
                className="hidden sm:block absolute bottom-0 right-[15%] w-[50%] h-[35%] rounded-3xl overflow-hidden shadow-2xl group transition-transform duration-700"
                style={{ animationDelay: '0.7s' }}
              >
                <img
                  src="https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?w=600&h=600&fit=crop&q=90"
                  alt="Modern wardrobe and storage"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-primary-400 rounded-full flex justify-center">
            <div className="w-1.5 h-3 bg-primary-600 rounded-full mt-2 animate-pulse" />
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;